'use client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ContextUserType,
  EmptyScreen,
  IConversationRestrictionOptions,
  IConversationSet,
  IMessage,
  ISelectedEmoji,
  TMessageAction,
  useMessageContext,
} from '@ui/components';
import { MessageItem } from './message-item';
import { twMerge } from 'tailwind-merge';
import { EmojiPickerActionSheet } from '@ui/components/moleculas/emoji-picker/emoji-picker-action-sheet';
import { MessageItemReportMessageActionSheet } from './message-item-report-message-action-sheet';
import { MessageItemMobileMoreMenuActionSheet } from './message-item-mobile-more-menu-action-sheet';
import { MessageListSkeleton } from './message-list-skeleton';
import useIntersectionObserver from '@ui/hooks/intersection-observer/intersection-observer';
import { getDayLabel } from '@ui/constants/date';
import { UnreadPill } from '../unread-pill';

// Helper functions for sessionStorage unread count management
const UNREAD_COUNT_KEY = 'conversation_unread_counts';

interface UnreadCounts {
  [conversationId: string]: number;
}

const getUnreadCounts = (): UnreadCounts => {
  try {
    const stored = sessionStorage.getItem(UNREAD_COUNT_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const getUnreadCount = (conversationId: string): number => {
  const counts = getUnreadCounts();
  return counts[conversationId] || 0;
};

interface Props {
  contextUserType: ContextUserType;
  restrictions: IConversationRestrictionOptions | undefined;
  conversationSet?: IConversationSet;
  singleConversationSet?: IConversationSet | null;
  className?: string;
  messageDescriptionClassName?: string;
  onScrollToBottom?: () => void;
  onReadNewMessage?: (conversation: IConversationSet) => void;
}
export const MessageList = ({
  className,
  contextUserType,
  restrictions,
  onScrollToBottom,
  onReadNewMessage,
  conversationSet,
  singleConversationSet,
  messageDescriptionClassName,
}: Props) => {
  const [selectedMessage, setSelectedMessage] = useState<IMessage | null>(null);
  const [openEmojiPicker, setOpenEmojiPicker] = useState(false);
  const [openReportMenu, setOpenReportMenu] = useState(false);
  const [openMobileMoreMenu, setOpenMobileMoreMenu] = useState(false);
  const messageState = useMessageContext(contextUserType);
  const messagesEndRef = useIntersectionObserver(onScrollToBottom, { threshold: 1.0 });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const prevScrollHeightRef = useRef(0);
  const prevScrollTopRef = useRef(0);
  const isLoadingOlderRef = useRef(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const prevMessageCountRef = useRef(0);
  const isAutoFillingRef = useRef(false);
  const [missingTargetNotice, setMissingTargetNotice] = useState<string | null>(null);

  // Get unread count from sessionStorage using helper function
  const conversationId =
    conversationSet?.conversation?.id || singleConversationSet?.conversation?.id || '';
  const unreadCount = getUnreadCount(conversationId);

  const scrollContainerToBottom = (behavior: ScrollBehavior = 'auto') => {
    const el = containerRef.current;
    if (!el) return;
    if (behavior === 'smooth') {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    } else {
      el.scrollTop = el.scrollHeight;
    }
  };

  const scrollToBottom = () => {
    scrollContainerToBottom('auto');
  };

  const onReactToMessage = (emoji: ISelectedEmoji) => {
    if (selectedMessage) {
      messageState.onReactToMessage(
        selectedMessage.id,
        selectedMessage.slug,
        'reaction',
        emoji.native,
        messageState.query,
      );
      setSelectedMessage(null);
    }
  };

  const onReactToMessageWithNativeEmoji = (emoji: string) => {
    if (selectedMessage) {
      messageState.onReactToMessage(
        selectedMessage.id,
        selectedMessage.slug,
        'reaction',
        emoji,
        messageState.query,
      );
      setSelectedMessage(null);
    }
  };

  const onCloseEmojiPicker = () => {
    setSelectedMessage(null);
    setOpenEmojiPicker(false);
  };

  const onCloseReport = () => {
    setSelectedMessage(null);
    setOpenReportMenu(false);
  };

  const onCloseMobileMenu = () => {
    setOpenMobileMoreMenu(false);
  };

  useEffect(() => {
    if (contextUserType === 'creator') {
      messageState.onSetOnboardingTriggerVisibility(false);
    }
  }, [contextUserType]);

  useEffect(() => {
    if (isLoadingOlderRef.current) return;
    if (messageState.messages.length > 0 && !messageState.isLoading) {
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [messageState.messages.length, messageState.isLoading]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const threshold = 100;
      if (
        el.scrollTop <= threshold &&
        messageState.hasMorePages &&
        !messageState.isFetching &&
        !isLoadingOlderRef.current
      ) {
        isLoadingOlderRef.current = true;
        setIsLoadingOlder(true);
        prevScrollHeightRef.current = el.scrollHeight;
        prevScrollTopRef.current = el.scrollTop;
        prevMessageCountRef.current = messageState.messages.length;
        messageState.onNextPage();
      }
    },
    [
      messageState.hasMorePages,
      messageState.isFetching,
      messageState.messages.length,
      messageState.onNextPage,
    ],
  );

  useEffect(() => {
    if (!isLoadingOlderRef.current) return;
    const el = containerRef.current;
    if (!el) return;
    const hasNewItems = messageState.messages.length > prevMessageCountRef.current;
    if (hasNewItems) {
      const delta = el.scrollHeight - prevScrollHeightRef.current;
      if (delta > 0) {
        el.scrollTop = prevScrollTopRef.current + delta;
      }
      isLoadingOlderRef.current = false;
      setIsLoadingOlder(false);
      prevMessageCountRef.current = messageState.messages.length;
    } else if (!messageState.isFetching) {
      isLoadingOlderRef.current = false;
      setIsLoadingOlder(false);
    }
  }, [messageState.messages.length, messageState.isFetching]);

  useEffect(() => {
    if (!isAutoFillingRef.current) return;
    scrollContainerToBottom('auto');
  }, [messageState.messages.length]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const buffer = 80;
    const isScrollable = el.scrollHeight > el.clientHeight + buffer;

    if (
      !isScrollable &&
      messageState.hasMorePages &&
      !messageState.isFetching &&
      !isLoadingOlderRef.current
    ) {
      isAutoFillingRef.current = true;
      prevMessageCountRef.current = messageState.messages.length;
      messageState.onNextPage();
    } else if (isScrollable || !messageState.hasMorePages) {
      isAutoFillingRef.current = false;
    }
  }, [messageState.messages.length, messageState.hasMorePages, messageState.isFetching]);

  const sortedMessages = useMemo(() => {
    return [...messageState.messages].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );
  }, [messageState.messages]);

  const handleMessageSelection = (message: IMessage, action: TMessageAction) => {
    setSelectedMessage(message);

    if (action === 'react') {
      setOpenEmojiPicker(true);
    }

    if (action === 'report') {
      setOpenReportMenu(true);
    }

    if (action === 'mobile-more-menu') {
      setOpenMobileMoreMenu(true);
    }
  };

  const onReportMessage = (report: string, message: IMessage) => {
    messageState.onReportMessage({ message_id: message.id, report });
    setSelectedMessage(null);
  };

  const onEditMessage = (message: IMessage) => {
    messageState.setMessageToEdit(message);

    if (message.miscellaneous?.reply_to && messageState.setReplyToMessage) {
      messageState.setReplyToMessage({
        username: message.miscellaneous.reply_to.username,
        message: message.miscellaneous.reply_to.message,
        messageId: message.miscellaneous.reply_to.messageId,
      });
    }

    messageState.onSetOnboardingTriggerVisibility(false);
  };

  const onReplyToMessage = (message: IMessage) => {
    const editing = (messageState as any)?.messageToEdit as IMessage | undefined;
    if (editing?.created_at && message?.created_at) {
      const editTime = new Date(editing.created_at).getTime();
      const replyTime = new Date(message.created_at).getTime();
      if (replyTime > editTime) {
        setMissingTargetNotice('Cannot reply to a newer message while editing');
        setTimeout(() => setMissingTargetNotice(null), 2500);
        return;
      }
    }

    if (messageState.setReplyToMessage) {
      messageState.setReplyToMessage({
        username: message.user_username,
        message: message.description,
        messageId: message.id,
      });
    }
  };

  const handleScrollToMessage = useCallback(
    (messageId: string) => {
      if (!containerRef.current) return;

      const messageElement = containerRef.current.querySelector(
        `[data-message-id="${messageId}"]`,
      ) as HTMLElement;

      if (messageElement) {
        messageElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        messageElement.style.transition = 'background-color 0.3s ease';
        messageElement.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
        setTimeout(() => {
          messageElement.style.backgroundColor = '';
        }, 2000);
      } else {
        setMissingTargetNotice('Message not found. The reply to message may be in an earlier page');
        setTimeout(() => setMissingTargetNotice(null), 2500);
      }
    },
    [containerRef],
  );

  // const shouldRender = useMemo(() => {
  //   return messageState.query.thread_id === messageState?.messages![0]?.thread_id;
  // }, [messageState.query.thread_id, messageState?.messages]);

  const shouldRender = useMemo(() => {
    const firstThreadId = messageState?.messages?.[0]?.thread_id;
    return messageState.messages.length === 0 || messageState.query.thread_id === firstThreadId;
  }, [messageState.query.thread_id, messageState.messages]);

  return (
    <>
      <div
        ref={containerRef}
        className={twMerge('h-full flex-grow space-y-4 overflow-y-auto', className)}
        onScroll={handleScroll}>
        {missingTargetNotice && (
          <div className="sticky top-2 z-50 flex justify-center">
            <div className="pointer-events-none rounded-full bg-gray-900/90 px-3 py-1 text-[11px] font-medium text-white shadow">
              {missingTargetNotice}
            </div>
          </div>
        )}
        {(isLoadingOlder || (messageState.isFetching && messageState.hasMorePages)) && (
          <div className="flex items-center justify-center py-2 text-xs text-gray-500">
            <span className="mr-2 inline-block h-3 w-3 animate-spin rounded-full border border-gray-300 border-t-gray-600" />
            Loading messages
          </div>
        )}
        {contextUserType === 'creator' &&
          !messageState.isLoading &&
          messageState.messages.length === 0 &&
          messageState.conversationMiscellaneous?.cohortInfo && (
            <div className="mx-auto max-w-sm">
              <EmptyScreen
                className="bg-transparent lg:bg-transparent"
                titleClassName="text-gray-500"
                title="You have not created any messages for this cohort yet."
              />
            </div>
          )}
        {messageState.isLoading ? (
          <MessageListSkeleton />
        ) : (
          <>
            {shouldRender &&
              (() => {
                const blocks: JSX.Element[] = [];
                let lastLabel: string | null = null;

                sortedMessages.forEach((message, idx) => {
                  const label = getDayLabel(message.created_at);
                  if (label !== lastLabel) {
                    blocks.push(
                      <div key={`divider-${label}-${idx}`} className="py-1">
                        <div className="mx-auto w-fit rounded-full bg-gray-200 px-3 py-1 text-[11px] font-medium text-gray-700">
                          {label}
                        </div>
                      </div>,
                    );
                    lastLabel = label;
                  }

                  // Show UnreadPill before the first unread message
                  // Position from bottom since latest messages are at the bottom
                  const totalMessages = sortedMessages.length;
                  const unreadPillPosition = totalMessages - unreadCount;
                  if (unreadCount > 0 && idx === unreadPillPosition) {
                    blocks.push(
                      <div key={`unread-pill-${idx}`}>
                        <UnreadPill unreadMessagesCount={unreadCount} />
                      </div>,
                    );
                  }

                  blocks.push(
                    <div key={message.id || idx} data-message-id={message.id}>
                      <MessageItem
                        restrictions={restrictions}
                        setSelectedMessage={handleMessageSelection}
                        message={message}
                        isCreator={messageState.user_id === message.creator_id}
                        onEdit={onEditMessage}
                        onReply={onReplyToMessage}
                        onScrollToMessage={handleScrollToMessage}
                        user_id={messageState.user_id}
                        user_username={messageState.user_username}
                        messageDescriptionClassName={messageDescriptionClassName}
                      />
                    </div>,
                  );
                });
                return blocks;
              })()}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      <EmojiPickerActionSheet
        message={selectedMessage as IMessage}
        open={openEmojiPicker}
        setOpen={onCloseEmojiPicker}
        onEmojiSelect={onReactToMessage}
      />

      <MessageItemReportMessageActionSheet
        message={selectedMessage as IMessage}
        open={openReportMenu}
        setOpen={onCloseReport}
        onReportMessage={onReportMessage}
      />

      <div className="md:hidden">
        <MessageItemMobileMoreMenuActionSheet
          message={selectedMessage as IMessage}
          setMessage={handleMessageSelection}
          open={openMobileMoreMenu}
          setOpen={onCloseMobileMenu}
          onEmojiSelect={onReactToMessageWithNativeEmoji}
          onReply={onReplyToMessage}
          onEdit={onEditMessage}
          canReply={contextUserType === 'creator' || !!restrictions?.fans_reply}
          user_id={messageState.user_id}
        />
      </div>
    </>
  );
};
